import { useEffect, useRef, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { cn } from '@/shared/lib/utils'

const PANEL_HEIGHT_CLASS = 'h-56'
export const COMPLIANCE_DOCUMENT_STREAMING_PANEL_TRANSITION_MS = 500

/** @deprecated Use COMPLIANCE_DOCUMENT_STREAMING_PANEL_TRANSITION_MS */
export const RISK_ASSESSMENT_STREAMING_PANEL_TRANSITION_MS =
  COMPLIANCE_DOCUMENT_STREAMING_PANEL_TRANSITION_MS

type ComplianceDocumentStreamingPanelProps = {
  isOpen: boolean
  statusLabel: string
  streamingContent: string
}

export function ComplianceDocumentStreamingPanel({
  isOpen,
  statusLabel,
  streamingContent,
}: ComplianceDocumentStreamingPanelProps) {
  const streamContainerRef = useRef<HTMLPreElement>(null)
  const [isMounted, setIsMounted] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [display, setDisplay] = useState({
    statusLabel: '',
    streamingContent: '',
  })

  useEffect(() => {
    if (isOpen) {
      setDisplay({ statusLabel, streamingContent })
      setIsMounted(true)
      const expandFrame = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsExpanded(true)
        })
      })
      return () => {
        cancelAnimationFrame(expandFrame)
      }
    }

    if (isMounted) {
      setIsExpanded(false)
      const collapseTimer = window.setTimeout(() => {
        setIsMounted(false)
      }, COMPLIANCE_DOCUMENT_STREAMING_PANEL_TRANSITION_MS)
      return () => {
        clearTimeout(collapseTimer)
      }
    }

    return undefined
  }, [isOpen, isMounted])

  useEffect(() => {
    if (isOpen) {
      setDisplay({ statusLabel, streamingContent })
    }
  }, [isOpen, statusLabel, streamingContent])

  useEffect(() => {
    if (streamContainerRef.current != null && isOpen) {
      streamContainerRef.current.scrollTop =
        streamContainerRef.current.scrollHeight
    }
  }, [display.streamingContent, isOpen])

  if (!isMounted) {
    return null
  }

  return (
    <div
      className={cn(
        'grid overflow-hidden transition-[grid-template-rows,margin-bottom,opacity] duration-500 ease-in-out',
        isExpanded
          ? 'mb-4 grid-rows-[1fr] opacity-100'
          : 'mb-0 grid-rows-[0fr] opacity-0'
      )}
      aria-hidden={!isExpanded}
    >
      <div className='min-h-0 overflow-hidden'>
        <div
          className={cn(
            'flex flex-col rounded-lg border bg-muted/40 shadow-sm transition-[transform,opacity] duration-500 ease-in-out',
            PANEL_HEIGHT_CLASS,
            isExpanded
              ? 'translate-y-0 opacity-100'
              : '-translate-y-4 opacity-0'
          )}
        >
          <div className='flex shrink-0 items-center gap-2 border-b px-4 py-2.5 text-sm'>
            {isOpen ? (
              <Loader2
                className='size-4 shrink-0 animate-spin text-primary'
                aria-hidden
              />
            ) : (
              <span className='size-4 shrink-0' aria-hidden />
            )}
            <span className='font-medium'>{display.statusLabel}</span>
          </div>
          <pre
            ref={streamContainerRef}
            className='min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-4 py-3 text-sm whitespace-pre-wrap'
          >
            {display.streamingContent}
            {isOpen ? (
              <span
                aria-hidden
                className='inline-block h-[1em] w-0.5 translate-y-0.5 animate-[blink_1s_step-end_infinite] bg-current align-text-bottom opacity-100'
              />
            ) : null}
          </pre>
        </div>
      </div>
    </div>
  )
}

/** @deprecated Use ComplianceDocumentStreamingPanel */
export const RiskAssessmentSectionStreamingPanel = ComplianceDocumentStreamingPanel
